import React from "react";
import { View, Text, Svg, Path, StyleSheet } from "@react-pdf/renderer";

const GOLD = "#F4D26C";
const WHITE = "#ffffff";
const DARK_TEXT = "#333333";

const s = StyleSheet.create({
  listContainer: { marginBottom: 6 },
  listItem: { flexDirection: "row", alignItems: "flex-start", marginBottom: 8, paddingRight: 8 },
  bulletWrap: { marginTop: 3, marginRight: 8, flexShrink: 0 },
  itemTextLight: { fontSize: 8.5, color: GOLD, lineHeight: 1.6, flex: 1, fontFamily: "NotoSans" },
  itemTextDark: { fontSize: 8.5, color: DARK_TEXT, lineHeight: 1.6, flex: 1, fontFamily: "NotoSans" },
});

/** Strip HTML tags and decode common HTML entities */
const stripHtml = (html: string): string =>
  html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s{2,}/g, " ")
    .trim();

/**
 * Split a paragraph of sentences into individual items.
 * Splits on ". " followed by a capital letter, or "! " or "? " followed by capital.
 */
const splitIntoSentences = (text: string): string[] => {
  const parts = text.split(/(?<=[.!?])\s{1,3}(?=[A-Z])/);
  return parts
    .map((p) => p.trim())
    .filter((p) => p.length > 10);
};

/** Extract bullet items from HTML — handles <li>, multiple <p>, single <p> split by sentence */
export const extractListItems = (html: string): string[] => {
  // 1. Proper <li> tags
  const liMatches = html.match(/<li[^>]*>([\s\S]*?)<\/li>/gi);
  if (liMatches && liMatches.length > 0) {
    return liMatches.map((m) => stripHtml(m)).filter(Boolean);
  }

  // 2. Multiple <p> tags — each <p> is one bullet
  const pMatches = html.match(/<p[^>]*>([\s\S]*?)<\/p>/gi);
  if (pMatches && pMatches.length > 1) {
    return pMatches.map((m) => stripHtml(m)).filter(Boolean);
  }

  // 3. Single <p> or plain text — split by sentence boundaries
  const rawText = stripHtml(html);
  if (!rawText) return [];

  const sentences = splitIntoSentences(rawText);
  if (sentences.length > 1) return sentences;

  // 4. Fallback: return as single item
  return rawText ? [rawText] : [];
};

interface IBulletListProps {
  html: string;
  /** Use dark text (for white backgrounds). Default false = gold text on dark bg */
  darkText?: boolean;
}

/** Renders HTML content as ▶ bullet rows. White triangle + gold text on dark bg, or black triangle + dark text on white bg */
export const BulletList: React.FC<IBulletListProps> = ({ html, darkText = false }) => {
  if (!html || html.trim() === "") return null;
  const items = extractListItems(html);
  if (items.length === 0) return null;

  const triangleColor = darkText ? "#000000" : WHITE;
  const textStyle = darkText ? s.itemTextDark : s.itemTextLight;

  return (
    <View style={s.listContainer}>
      {items.map((item, i) => (
        <View style={s.listItem} key={i} wrap={false}>
          <View style={s.bulletWrap}>
            <Svg width="7" height="8" viewBox="0 0 7 8">
              <Path d="M0 0 L7 4 L0 8 Z" fill={triangleColor} />
            </Svg>
          </View>
          <Text style={textStyle}>{item}</Text>
        </View>
      ))}
    </View>
  );
};
